import { Stack, TextField, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { ViewModel } from '../../../../../models/view.model';
import { ConfirmDeletionDialog } from '../../../../Dialog/ConfirmDialog/ConfirmDeletionDialog';

interface DeleteViewConfirmDialogProps {
  view: ViewModel | null;
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
}

export function DeleteViewConfirmDialog({ view, open, onClose, onConfirm }: DeleteViewConfirmDialogProps) {
  const [typed, setTyped] = useState('');

  useEffect(() => {
    if (!open) {
      setTyped('');
    }
  }, [open]);

  if (!view) {
    return null;
  }

  const isDefaultView = view.isDefault === true;
  const viewName = view.name;

  return (
    <ConfirmDeletionDialog
      open={open}
      onClose={onClose}
      onConfirm={onConfirm}
      title={isDefaultView ? `Delete default view "${viewName}"?` : `Delete View "${viewName}"?`}
      confirmLabel={isDefaultView ? 'Yes, Delete Default View' : undefined}
      confirmDisabled={isDefaultView && typed !== viewName}
    >
      {isDefaultView ? (
        <DefaultViewDeleteBody name={viewName} typed={typed} onTypedChange={setTyped} />
      ) : (
        <Typography variant='body2' color='text.secondary'>
          The view will be deleted permanently
        </Typography>
      )}
    </ConfirmDeletionDialog>
  );
}

interface DefaultViewDeleteBodyProps {
  name: string;
  typed: string;
  onTypedChange: (value: string) => void;
}

function DefaultViewDeleteBody({ name, typed, onTypedChange }: DefaultViewDeleteBodyProps) {
  return (
    <Stack gap={2}>
      <Typography variant='body2' color='text.secondary'>
        This is a default view. Deleting it removes it for everyone in your organization. This cannot be
        undone.
      </Typography>
      <Typography variant='body2'>
        Type <strong>{name}</strong> to confirm:
      </Typography>
      <TextField
        value={typed}
        onChange={(e) => onTypedChange(e.target.value)}
        size='small'
        autoFocus
        fullWidth
        inputProps={{ 'aria-label': 'Confirm view name' }}
      />
    </Stack>
  );
}
